import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import { firebaseFirestore, firebaseFunctions } from 'services/firebase';

import { notification } from 'antd';

import { intl } from 'utils/intl';
import downloadCSV from 'utils/downloadCSV';

import { setMemberState } from './actions';

import { getMembersWithCities } from './utils';

import MemberActionTypes from './types';

export function* fetchMembersAsync({ payload }) {
  yield put(setMemberState({ loading: payload === '', searching: payload !== '' }));

  const { pageSize } = yield select(state => state.member);

  try {
    const searchQueryRef = yield firebaseFirestore
      .collection('userDetails')
      .where('keywords', 'array-contains', payload.toLowerCase())
      .orderBy('registeredDate')
      .limit(pageSize);
    const searchQuerySnapshot = yield searchQueryRef.get();

    const members = yield getMembersWithCities(searchQuerySnapshot);

    yield put(
      setMemberState({
        loading: false,
        searching: false,
        membersData: members,
        hasNextPage: !(members.length < pageSize),
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'members.notification.error.title' }),
      description: intl.formatMessage({
        id: 'members.notification.fetch.error',
      }),
    });
    yield put(setMemberState({ loading: false }));
  }
}

export function* nextPage() {
  const { membersData, currentPage, pageSize, searchQuery } = yield select(state => state.member);

  const lastVisible = membersData[membersData.length - 1];

  yield put(setMemberState({ loading: true }));

  try {
    const lastVisibleDoc = yield firebaseFirestore
      .collection('userDetails')
      .doc(lastVisible.id)
      .get();

    const query = firebaseFirestore
      .collection('userDetails')
      .where('keywords', 'array-contains', searchQuery.toLowerCase())
      .orderBy('registeredDate')
      .startAfter(lastVisibleDoc)
      .limit(pageSize);

    const snapshot = yield query.get();

    const members = yield getMembersWithCities(snapshot);

    yield put(
      setMemberState({
        membersData: members.length > 0 ? members : membersData,
        hasNextPage: members.length > 0,
        currentPage: members.length > 0 ? currentPage + 1 : currentPage,
        loading: false,
      }),
    );

    if (members.length === 0) {
      yield notification.warn({
        message: intl.formatMessage({ id: 'members.notification.pagination.noData.title' }),
        description: intl.formatMessage({
          id: 'members.notification.pagination.noData.description',
        }),
      });
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'members.notification.error.title' }),
      description: intl.formatMessage({
        id: 'members.notification.pagination.error',
      }),
    });
    yield put(setMemberState({ loading: false }));
  }
}

export function* prevPage() {
  const { membersData, searchQuery, currentPage, pageSize } = yield select(state => state.member);

  const firstVisible = membersData[0];

  yield put(setMemberState({ loading: true }));

  try {
    const firstVisibleDoc = yield firebaseFirestore
      .collection('userDetails')
      .doc(firstVisible.id)
      .get();

    const query = firebaseFirestore
      .collection('userDetails')
      .where('keywords', 'array-contains', searchQuery.toLowerCase())
      .orderBy('registeredDate')
      .endBefore(firstVisibleDoc)
      .limitToLast(pageSize);

    const snapshot = yield query.get();

    const members = yield getMembersWithCities(snapshot);

    yield put(
      setMemberState({
        membersData: members.length > 0 ? members : membersData,
        currentPage: members.length > 0 ? currentPage - 1 : currentPage,
        hasNextPage: true,
        loading: false,
      }),
    );
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'members.notification.error.title' }),
      description: intl.formatMessage({
        id: 'members.notification.pagination.error',
      }),
    });
    yield put(setMemberState({ loading: false }));
  }
}

export function* filterMembers({ payload }) {
  const { dateFrom, dateTo } = payload;

  yield put(setMemberState({ isFilteringMembers: true }));

  try {
    const getPlayersForDateRange = yield firebaseFunctions.httpsCallable('getPlayersForDateRange');
    const { data } = yield getPlayersForDateRange({ dateFrom, dateTo });

    if (data.success) {
      if (data.length === 0) {
        notification.error({
          message: intl.formatMessage({ id: 'members.filter.notification.error.title' }),
          description: intl.formatMessage({ id: 'members.filter.notification.noData.desription' }),
        });
      } else {
        yield call(downloadCSV, {
          filename: `patos-users-${new Date().getTime()}.csv`,
          data: data.data,
          columns: ['id', 'name', 'surname', 'phone'],
        });
      }

      yield put(setMemberState({ isFilteringMembers: false }));
    } else {
      throw new Error();
    }
  } catch (error) {
    notification.error({
      message: intl.formatMessage({ id: 'members.filter.notification.error.title' }),
      description: intl.formatMessage({ id: 'members.filter.notification.filter.description' }),
    });
    yield put(setMemberState({ isFilteringMembers: false }));
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(MemberActionTypes.FETCH_MEMBERS_START, fetchMembersAsync),
    takeEvery(MemberActionTypes.NEXT_PAGE, nextPage),
    takeEvery(MemberActionTypes.PREV_PAGE, prevPage),
    takeEvery(MemberActionTypes.FILTER_MEMBERS, filterMembers),
  ]);
}
